import defaults from 'lodash.defaults';
import find from 'lodash.find';
import findIndex from 'lodash.findindex';
import omit from 'lodash.omit';
import slice from 'lodash.slice';

/**
 * Helper function - return a new array of activity objects, where the activity
 * with the given id is overridden with key-value pairs specified in update.
 *
 * @param {array} activities
 * @param {number} id
 * @param {object} update
 * @return {array}
 */
const updateActivityInList = (activities, id, update) => {
  const index = findIndex(activities, (a) => a.id === id);

  const newer = slice(activities, 0, index);
  const older = slice(activities, index + 1);
  const updatedActivity = defaults(update, activities[index]);

  return [...newer, updatedActivity, ...older];
};

/**
 * Helper function for diffing activity arrays
 *
 * @param {array} newActivities
 * @param {array} existingActivities
 * @return {array} - the newActivities that aren't duplicates
 */
const filterDuplicatesById = (newActivities, existingActivities) => {
  const notADuplicate = (activity) => {
    return !find(existingActivities, { id: activity.id });
  };

  return newActivities.filter(notADuplicate);
};

/**
 * Return a new state object so that the given activity
 * has { acknowledged: true }
 *
 * @param {object} state
 * @param {object} payload
 * @return {object}
 */
const handleActivityAcknowledged = (state, { id }) => {
  const updatedActivities = updateActivityInList(state.activities, id, {
    acknowledged: true,
  });

  return defaults({ activites: updatedActivities }, state);
};

/**
 * Loop through existing activities, return a new state object
 * in which all those activities have { displayed: true }
 *
 * @param {object} state
 * @return {object}
 */
const handleActivitiesDisplayed = (state) => {
  const markDisplayed = (activity) => defaults({ displayed: true }, activity);

  return defaults(
    {
      activities: state.activities.map(markDisplayed),
      shouldIndicateUndisplayedActivities: false,
    },
    state,
  );
};

/**
 * Add userId to pending follows
 *
 * @param {object} state
 * @param {number} userId
 * @return {object}
 */
const handleStartToggleFollow = (state, userId) => {
  const updatedPendingFollows = defaults(
    {
      [userId]: true,
    },
    state.pendingFollows,
  );

  return defaults({ pendingFollows: updatedPendingFollows }, state);
};

/**
 * Toggle true/false for the value indicating 'isFollowing' for the
 * follow button cta in activities matching userId and remove userId from
 * pending follows
 *
 * @param {object} state
 * @param {number} userId
 * @return {object}
 */
const handleCompleteToggleFollow = (state, userId) => {
  const toggleFollowByUserId = (activity) => {
    if (activity.cta_follower_id !== userId) {
      return activity;
    }

    return defaults(
      {
        cta_is_following: !activity.cta_is_following,
      },
      activity,
    );
  };

  return defaults(
    {
      activities: state.activities.map(toggleFollowByUserId),
      pendingFollows: omit(state.pendingFollows, [userId]),
    },
    state,
  );
};

/**
 * @param {object} state
 * @param {object} payload
 * @param {number} pageSize - the number of activities requested
 * @return {object}
 */
const appendActivities = (state, { activities, pageSize }) => {
  // In theory, we won't get duplicates on a request for older activities
  // But if you're going crazy with the scroll bar, you can manage to fire
  // multiple requests with the same id, this dedupe guards against that
  const dedupedActivities = filterDuplicatesById(activities, state.activities);

  const newState = defaults(
    {
      activities: state.activities.concat(dedupedActivities),
    },
    state,
  );

  const hasExistingUndisplayed = state.shouldIndicateUndisplayedActivities;

  const hasNewUndisplayed = !!dedupedActivities.find((activity) => {
    return !activity.displayed;
  });

  return defaults(
    {
      noOlderActivitiesExist: activities.length < pageSize,
      isRequestingOlderActivities: false,
      shouldIndicateUndisplayedActivities: hasExistingUndisplayed || hasNewUndisplayed,
    },
    newState,
  );
};

/**
 * @param {object} state
 * @param {object} payload
 * @return {object}
 */
const prependActivities = (state, { activities }) => {
  const dedupedActivities = filterDuplicatesById(activities, state.activities);

  const newState = defaults({ activities: dedupedActivities.concat(state.activities) }, state);

  const hasExistingUndisplayed = state.shouldIndicateUndisplayedActivities;

  const hasNewUndisplayed = !!dedupedActivities.find((activity) => {
    return !activity.displayed;
  });

  return defaults(
    {
      shouldIndicateUndisplayedActivities: hasExistingUndisplayed || hasNewUndisplayed,
    },
    newState,
  );
};

const reducer = (state, { type, payload, error, meta }) => {
  switch (type) {
    case 'RECEIVE_NEW_ACTIVITIES':
      return error ? state : prependActivities(state, payload);
    case 'RECEIVE_OLDER_ACTIVITIES':
      return error ? state : appendActivities(state, payload);
    case 'REQUEST_OLDER_ACTIVITIES':
      return defaults({ isRequestingOlderActivities: true }, state);
    case 'DONE_REQUESTING_OLDER_ACTIVITIES':
      return defaults({ isRequestingOlderActivities: false }, state);
    case 'ACTIVITY_ACKNOWLEDGED':
      return handleActivityAcknowledged(state, payload);
    case 'ACTIVITIES_DISPLAYED':
      return handleActivitiesDisplayed(state);
    case 'START_TOGGLE_FOLLOW':
      return handleStartToggleFollow(state, payload);
    case 'COMPLETE_TOGGLE_FOLLOW':
      return handleCompleteToggleFollow(state, payload);
    default:
      return state;
  }
};

export default reducer;
